(() => {
  if (window.__TMP_MELLER_2PLUS1_BUILDER__) return;
  window.__TMP_MELLER_2PLUS1_BUILDER__ = true;

  const SELECTORS = {
    template: '[data-tmp-bundle-template]',
    root: '[data-tmp-bundle-root]'
  };

  const money = (cents) => {
    const value = (Number(cents) || 0) / 100;
    const hasDecimals = Math.abs(value % 1) > 0.001;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-NL', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0
      }).format(value);
    } catch (_) {
      return `€${value.toFixed(hasDecimals ? 2 : 0)}`;
    }
  };

  const centsFromLiquid = (value) => Math.round(Number(value) || 0);
  const imageSrc = (image) => {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  };

  const productLabels = (title = '') => {
    const raw = String(title).trim();
    const match = raw.match(/^(.*?)\s*\((.*?)\)\s*$/);
    if (match) return { model: match[1].trim(), colorway: match[2].trim() };
    const dash = raw.split(/\s+[–—-]\s+/);
    if (dash.length > 1) return { model: dash.shift().trim(), colorway: dash.join(' - ').trim() };
    return { model: raw, colorway: '' };
  };

  class MellerBundle {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.rail = root.querySelector('[data-product-rail]');
      this.stageTitle = root.querySelector('[data-stage-title]');
      this.stageMeta = root.querySelector('[data-stage-meta]');
      this.note = root.querySelector('[data-bundle-note]');
      this.changeSecond = root.querySelector('[data-change-second]');
      this.cta = root.querySelector('[data-bundle-cta]');
      this.error = root.querySelector('[data-bundle-error]');
      this.progressSecond = root.querySelector('[data-progress-second]');
      this.progressFree = root.querySelector('[data-progress-free]');
      this.products = null;
      this.stage = 2;
      this.batch = 24;
      this.rendered = 0;
      this.visible = [];
      this.loading = false;

      this.selections = {
        1: {
          variantId: String(root.dataset.currentVariantId || ''),
          productId: String(root.dataset.currentProductId || ''),
          title: root.dataset.currentTitle || '',
          cents: centsFromLiquid(root.dataset.currentPrice),
          image: root.dataset.currentImage || ''
        },
        2: null,
        3: null
      };

      this.bind();
      this.refresh();
      this.loadProducts().then(() => this.renderStage()).catch(() => this.setError('Could not load shades. Please refresh.'));
    }

    bind() {
      this.rail?.addEventListener('scroll', () => {
        if (!this.rail || this.rendered >= this.visible.length) return;
        const remaining = this.rail.scrollWidth - this.rail.scrollLeft - this.rail.clientWidth;
        if (remaining < 500) this.appendBatch();
      }, { passive: true });

      this.changeSecond?.addEventListener('click', () => {
        this.stage = 2;
        this.selections[2] = null;
        this.selections[3] = null;
        this.renderStage(true);
        this.refresh();
      });

      this.cta?.addEventListener('click', () => {
        if (!this.selections[2]) return this.addSingle();
        if (!this.selections[3]) return this.focusStage(3);
        this.addBundle();
      });

      document.addEventListener('variant:changed', (event) => {
        const variant = event.detail?.variant;
        if (!variant) return;
        const id = String(variant.id || '');
        const currentInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        if (currentInput && String(currentInput.value) !== id) return;

        this.selections[1] = {
          ...this.selections[1],
          variantId: id,
          cents: centsFromLiquid(variant.price),
          image: imageSrc(variant.featured_image) || this.selections[1].image
        };

        if (this.selections[2] && this.selections[3]) {
          const cap = Math.min(this.selections[1].cents, this.selections[2].cents);
          if (this.selections[3].cents > cap) {
            this.selections[3] = null;
            this.stage = 3;
            this.renderStage(true);
          }
        }
        this.refresh();
      });
    }

    async loadProducts() {
      if (this.products) return this.products;
      this.loading = true;
      this.root.classList.add('is-loading');
      const response = await fetch(this.root.dataset.productsUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not load shades');
      const payload = await response.json();
      const source = Array.isArray(payload.products) ? payload.products : [];

      this.products = source.map((product) => {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const variant = variants.find((item) => item.available !== false);
        if (!variant) return null;
        const rawPrice = variant.price ?? product.price ?? 0;
        const cents = Math.round(Number.parseFloat(String(rawPrice)) * 100);
        const labels = productLabels(product.title || '');
        const image = imageSrc(variant.featured_image) || imageSrc(product.featured_image) || imageSrc(product.images?.[0]);
        return {
          productId: String(product.id || ''),
          variantId: String(variant.id || ''),
          title: product.title || '',
          model: labels.model,
          colorway: labels.colorway,
          cents,
          image
        };
      }).filter((product) => product && product.variantId && product.cents > 0 && product.image);

      this.loading = false;
      this.root.classList.remove('is-loading');
      return this.products;
    }

    eligibleProducts() {
      if (!this.products) return [];
      if (this.stage !== 3 || !this.selections[2]) return this.products;
      const cap = Math.min(this.selections[1].cents, this.selections[2].cents);
      return this.products.filter((product) => product.cents <= cap);
    }

    renderStage(resetScroll = false) {
      if (!this.products || !this.rail) return;
      this.visible = this.eligibleProducts();
      this.rendered = 0;
      this.rail.innerHTML = '';
      if (resetScroll) this.rail.scrollLeft = 0;
      this.appendBatch();
      this.refresh();
    }

    appendBatch() {
      if (!this.rail || this.rendered >= this.visible.length) return;
      const next = this.visible.slice(this.rendered, this.rendered + this.batch);
      const fragment = document.createDocumentFragment();
      next.forEach((product) => fragment.appendChild(this.card(product)));
      this.rail.appendChild(fragment);
      this.rendered += next.length;
    }

    card(product) {
      const selectedSecond = this.selections[2]?.variantId === product.variantId;
      const selectedFree = this.selections[3]?.variantId === product.variantId;
      const selected = selectedSecond || selectedFree;
      const card = document.createElement('article');
      card.className = `tmp-meller-card${selected ? ' is-selected' : ''}`;
      card.dataset.variantId = product.variantId;

      const imgWrap = document.createElement('div');
      imgWrap.className = 'tmp-meller-card__image';
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.model || product.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      imgWrap.appendChild(img);

      const info = document.createElement('div');
      info.className = 'tmp-meller-card__info';
      const name = document.createElement('strong');
      name.textContent = product.model || product.title;
      const color = document.createElement('span');
      color.textContent = product.colorway || ' ';
      const price = document.createElement('span');
      price.className = 'tmp-meller-card__price';
      price.textContent = this.stage === 3 ? `${money(product.cents)} → FREE` : money(product.cents);
      info.append(name, color, price);

      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'tmp-meller-card__choose';
      choose.textContent = selected ? 'SELECTED' : 'CHOOSE';
      choose.setAttribute('aria-pressed', selected ? 'true' : 'false');
      choose.addEventListener('click', () => this.select(product));

      card.append(imgWrap, info, choose);
      return card;
    }

    select(product) {
      this.clearError();
      if (this.stage === 2) {
        this.selections[2] = { ...product };
        this.selections[3] = null;
        this.stage = 3;
        this.root.dispatchEvent(new CustomEvent('tmp:second_pair_selected', { bubbles: true, detail: { variantId: product.variantId } }));
        this.renderStage(true);
        window.requestAnimationFrame(() => this.rail?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
      } else {
        this.selections[3] = { ...product };
        this.root.dispatchEvent(new CustomEvent('tmp:free_pair_selected', { bubbles: true, detail: { variantId: product.variantId } }));
        this.renderStage(false);
      }
      this.refresh();
    }

    refresh() {
      const hasSecond = Boolean(this.selections[2]);
      const hasFree = Boolean(this.selections[3]);

      this.progressSecond?.classList.toggle('is-done', hasSecond);
      this.progressFree?.classList.toggle('is-done', hasFree);
      if (this.changeSecond) this.changeSecond.hidden = !hasSecond;

      if (!hasSecond) {
        if (this.stageTitle) this.stageTitle.textContent = 'CHOOSE YOUR SECOND PAIR';
        if (this.stageMeta) this.stageMeta.textContent = 'OPTIONAL — OR ADD THIS PAIR NOW.';
        if (this.note) this.note.textContent = 'BUY 2 GET 1 FREE, OR BUY THIS PAIR ON ITS OWN.';
      } else if (!hasFree) {
        const cap = Math.min(this.selections[1].cents, this.selections[2].cents);
        if (this.stageTitle) this.stageTitle.textContent = 'NOW CHOOSE YOUR FREE PAIR';
        if (this.stageMeta) this.stageMeta.textContent = `CHOOSE ANY PAIR UP TO ${money(cap)}.`;
        if (this.note) this.note.textContent = `${this.selections[2].model || 'SECOND PAIR'} SELECTED ✓`;
      } else {
        const full = this.selections[1].cents + this.selections[2].cents + this.selections[3].cents;
        const saving = Math.min(this.selections[1].cents, this.selections[2].cents, this.selections[3].cents);
        if (this.stageTitle) this.stageTitle.textContent = 'YOUR 2+1 IS READY';
        if (this.stageMeta) this.stageMeta.textContent = `${money(full - saving)} TOTAL · YOU SAVE ${money(saving)}`;
        if (this.note) this.note.textContent = 'YOUR CHEAPEST PAIR IS FREE — NO CODE NEEDED.';
      }

      if (this.cta) {
        this.cta.disabled = this.loading;
        if (this.loading) this.cta.textContent = 'LOADING…';
        else if (hasSecond && !hasFree) this.cta.textContent = 'CHOOSE FREE PAIR';
        else this.cta.textContent = 'ADD TO CART';
        this.cta.classList.toggle('is-ready', hasSecond && hasFree);
      }
    }

    focusStage(stage) {
      this.clearError();
      this.stage = stage;
      if (stage === 2 && this.selections[2]) {
        this.selections[2] = null;
        this.selections[3] = null;
      }
      if (stage === 3 && !this.selections[2]) this.stage = 2;
      this.renderStage(true);
      this.setError(this.stage === 2 ? 'Choose your second pair first.' : 'Choose your free pair first.');
      this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.root.classList.add('tmp-bundle-attention');
      window.setTimeout(() => this.root.classList.remove('tmp-bundle-attention'), 700);
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    async addItems(items, loadingLabel, successEvent) {
      if (!this.cta) return;
      this.clearError();
      this.cta.disabled = true;
      this.cta.classList.add('is-loading');
      this.cta.textContent = loadingLabel;

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections }
      }));

      try {
        const response = await fetch(this.root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Could not add to cart.');

        this.cta.textContent = 'ADDED TO CART ✓';
        this.root.dispatchEvent(new CustomEvent(successEvent, { bubbles: true }));
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: payload }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        window.setTimeout(() => {
          this.cta.disabled = false;
          this.cta.classList.remove('is-loading');
          this.refresh();
        }, 1300);
      } catch (error) {
        this.setError(error.message || 'Something went wrong. Please try again.');
        this.cta.disabled = false;
        this.cta.classList.remove('is-loading');
        this.refresh();
      }
    }

    async addSingle() {
      const id = Number(this.selections[1]?.variantId);
      if (!id) return this.setError('Please select an available option first.');
      return this.addItems([{ id, quantity: 1 }], 'ADDING…', 'tmp:single_added');
    }

    async addBundle() {
      if (!this.selections[1] || !this.selections[2] || !this.selections[3]) return;
      const quantities = new Map();
      Object.values(this.selections).forEach((selection) => {
        const id = Number(selection.variantId);
        if (!id) return;
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      return this.addItems(
        Array.from(quantities.entries()).map(([id, quantity]) => ({ id, quantity })),
        'ADDING 3 SHADES…',
        'tmp:bundle_added'
      );
    }
  }

  const mount = (template) => {
    const sectionId = template.dataset.sectionId;
    const info = document.getElementById(`ProductInfo-${sectionId}`);
    if (!info || info.querySelector(SELECTORS.root)) return;

    const fragment = template.content.cloneNode(true);
    const root = fragment.querySelector(SELECTORS.root);
    if (!root) return;

    const priceBlock = info.querySelector('.product__block--price');
    const colorwayBlock = info.querySelector('[data-tmp-colorway-selector]')?.closest('.product__block') || info.querySelector('.tmp-shades-colorway-selector-block');
    const variantBlock = info.querySelector('.product__block--variant_picker');
    const anchor = colorwayBlock || variantBlock || priceBlock;
    if (anchor) anchor.after(root);
    else info.querySelector('.product__blocks')?.appendChild(root);

    root.dataset.sectionId = sectionId;
    document.body.classList.add('tmp-2plus1-product');
    new MellerBundle(root);
  };

  const init = (scope = document) => scope.querySelectorAll(SELECTORS.template).forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
